/**
 * A geometria dos terminais de um componente.
 *
 * **É a edição que esperava o desenho.** Altura, deslocar, posição e ramificação
 * não se escolhem olhando para uma tabela — se escolhem olhando para o traço. Com
 * o botão "Ver como ficaria" no painel do desenho, passam a se escolher.
 *
 * **O `PUT` é substituição completa**, e não de um terminal: dos sete campos de
 * todos eles, mais a cor e o desenho interno do componente, mais a ordem das duas
 * colunas. Por isso a tela carrega tudo antes e devolve tudo — inclusive `cor` e
 * `desenhoInternoId`, que vêm da linha do componente e não aparecem aqui. Sem
 * eles no corpo, mexer numa altura limparia a cor.
 *
 * **Duas colunas, duas sequências.** `ligacao` e `oposto` são numeradas
 * separadamente, a partir de 1, na ordem em que os ids chegam. Mover um terminal
 * mexe só na sua coluna; trocar a posição dele o manda para o fim da outra.
 *
 * **A ordem se arrasta**, segurando o pino ou o "liga em": levar o pino 25 para
 * o topo era apertar a seta de subir uma vez por linha. As setas ficam para o
 * ajuste de uma posição.
 */

import { useEffect, useMemo, useState } from "react"
import {
  TIPOS_DE_CIRCUITO, usarEditarTerminais, usarTerminaisDoComponente,
  type TerminalDoComponente,
} from "@/api/terminaisDoComponente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível gravar."
}

type Rascunho = TerminalDoComponente & { mexido: boolean }

/**
 * Leva o terminal `id` para o lugar de `alvoId`, dentro da coluna dos dois — o
 * arrastar da tabela. Descendo, ele cai depois do alvo; subindo, antes, que é
 * o que o olho espera de quem solta em cima de uma linha.
 *
 * **Só dentro da mesma coluna**: `ligacao` e `oposto` são numeradas em separado,
 * e trocar de coluna é o seletor da linha. Alvo da outra coluna não mexe em nada.
 */
export function moverPara<T extends { id: number; naColunaLigacao: boolean }>(
  ls: T[], id: number, alvoId: number,
): T[] {
  const alvo = ls.find((l) => l.id === id)
  const destino = ls.find((l) => l.id === alvoId)
  if (!alvo || !destino || alvo === destino || alvo.naColunaLigacao !== destino.naColunaLigacao) return ls

  const mesma = ls.filter((l) => l.naColunaLigacao === alvo.naColunaLigacao)
  const j = mesma.indexOf(destino)
  const nova = mesma.filter((l) => l !== alvo)
  nova.splice(j, 0, alvo)

  // remonta a lista inteira preservando a posicao relativa da outra coluna
  let k = 0
  return ls.map((l) => l.naColunaLigacao === alvo.naColunaLigacao ? nova[k++]! : l)
}

export function TerminaisDoComponente({
  projetoId, diagramaId, componenteId, cor, desenhoInternoId, aoFechar, aoGravar,
}: {
  projetoId: number
  diagramaId: number
  componenteId: number
  /** Do componente, não do terminal — vai no mesmo corpo do `PUT`. */
  cor: string
  desenhoInternoId: number
  /** Sem ele, não há botão de fechar — quem abre e fecha é a seção em volta. */
  aoFechar?: () => void
  /** Depois de gravar: quem está em volta redesenha (a montagem recarrega os traços). */
  aoGravar?: (recado: string) => void
}) {
  const { data, isPending, error } = usarTerminaisDoComponente(componenteId)
  const gravar = usarEditarTerminais(projetoId, diagramaId, componenteId)

  const [linhas, setLinhas] = useState<Rascunho[]>([])
  const [recado, setRecado] = useState<string | null>(null)

  useEffect(() => {
    if (data) setLinhas(data.map((t) => ({ ...t, mexido: false })))
  }, [data])

  const mexidos = useMemo(() => linhas.filter((l) => l.mexido).length, [linhas])

  function mudar(id: number, campo: Partial<TerminalDoComponente>) {
    setRecado(null)
    setLinhas((ls) => ls.map((l) => l.id === id ? { ...l, ...campo, mexido: true } : l))
  }

  /** Move dentro da própria coluna: a ordem das duas é numerada em separado. */
  function mover(id: number, direcao: -1 | 1) {
    setRecado(null)
    setLinhas((ls) => {
      const alvo = ls.find((l) => l.id === id)
      if (!alvo) return ls

      const mesma = ls.filter((l) => l.naColunaLigacao === alvo.naColunaLigacao)
      const i = mesma.indexOf(alvo)
      const j = i + direcao
      if (j < 0 || j >= mesma.length) return ls

      const nova = [...mesma]
      nova[i] = mesma[j]!
      nova[j] = alvo

      // remonta a lista inteira preservando a posicao relativa da outra coluna
      let k = 0
      return ls.map((l) => l.naColunaLigacao === alvo.naColunaLigacao ? nova[k++]! : l)
    })
  }

  /** O arrastar: quem está sendo levado e sobre qual linha ele está agora. */
  const [arrastando, setArrastando] = useState<number | null>(null)
  const [sobre, setSobre] = useState<number | null>(null)

  function soltarEm(alvoId: number) {
    const id = arrastando
    setArrastando(null); setSobre(null)
    if (id == null || id === alvoId) return
    setRecado(null)
    setLinhas((ls) => {
      const nova = moverPara(ls, id, alvoId)
      return nova === ls ? ls : nova.map((l) => l.id === id ? { ...l, mexido: true } : l)
    })
  }

  function trocarColuna(id: number, paraLigacao: boolean) {
    mudar(id, { posicao: paraLigacao ? "ligacao" : "oposto", naColunaLigacao: paraLigacao })
  }

  function salvar() {
    setRecado(null)
    gravar.mutate(
      {
        cor,
        desenhoInternoId,
        terminais: linhas.map((l) => ({
          diagramaLigacaoId: l.id,
          texto: l.texto,
          altura: l.altura,
          deslocar: l.deslocar,
          ramificacaoPosicao: l.ramificacaoPosicao,
          posicao: l.posicao,
          tipoCircuito: l.tipoCircuito,
          apelidoCircuito: l.apelidoCircuito,
        })),
        ordem: linhas.map((l) => ({ diagramaLigacaoId: l.id, ligacao: l.naColunaLigacao })),
      },
      {
        onSuccess: (r) => {
          const vizinhos = r.componentesParaAtualizar.length
          const texto = aoGravar
            ? `Ligações gravadas — a montagem foi redesenhada${vizinhos > 0
                ? `, com ${vizinhos} componente(s) vizinho(s)` : ""}. O desenho publicado só muda no Redesenhar.`
            : vizinhos > 0
              ? `Gravado. ${vizinhos} componente vizinho também mudou — o desenho precisa ser refeito.`
              : "Gravado. O desenho gravado ficou para trás."
          setRecado(texto)
          aoGravar?.(texto)
        },
        onError: (e) => setRecado(mensagem(e)),
      },
    )
  }

  const celula: React.CSSProperties = { padding: "3px 5px", fontSize: 11, verticalAlign: "middle" }

  return (
    <section style={{
      marginTop: 10, padding: 11, borderRadius: 8,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <header style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <b style={{ fontSize: 12 }}>Terminais do componente {componenteId}</b>
        <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
          {linhas.length} {linhas.length === 1 ? "terminal" : "terminais"}
          {mexidos > 0 ? ` · ${mexidos} alterado${mexidos === 1 ? "" : "s"}` : ""}
          {linhas.length > 1 && " · segure o pino ou o “liga em” e arraste para mudar a ordem"}
        </span>
        <span style={{ flex: 1 }} />
        {aoFechar && <button onClick={aoFechar} style={{ fontSize: 11 }}>fechar</button>}
      </header>

      {isPending && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--txt2)" }}>Carregando os terminais…</p>
      )}
      {error && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--erro)" }}>{mensagem(error)}</p>
      )}

      {linhas.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ color: "var(--txt3)", textAlign: "left" }}>
                <th style={celula}>coluna</th>
                <th style={celula}>pino</th>
                <th style={celula}>liga em</th>
                <th style={celula}>texto</th>
                <th style={celula}>altura</th>
                <th style={celula}>desl.</th>
                <th style={celula}>ram.</th>
                <th style={celula}>circuito</th>
                <th style={celula}>apelido</th>
                <th style={celula}>ordem</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => {
                const levado = linhas.findIndex((x) => x.id === arrastando)
                const daColuna = levado >= 0 && linhas[levado]!.naColunaLigacao === l.naColunaLigacao
                // A marca de onde vai cair: em cima subindo, embaixo descendo.
                const marca = sobre === l.id && arrastando !== l.id && daColuna
                  ? (levado > i ? "inset 0 2px 0 0 var(--acao)" : "inset 0 -2px 0 0 var(--acao)")
                  : undefined
                const alca = {
                  draggable: true,
                  onDragStart: (ev: React.DragEvent) => {
                    ev.dataTransfer.effectAllowed = "move"
                    ev.dataTransfer.setData("text/plain", String(l.id))
                    setArrastando(l.id)
                  },
                  onDragEnd: () => { setArrastando(null); setSobre(null) },
                  title: "Segure e arraste para mudar a ordem",
                }
                return (
                <tr key={l.id}
                  onDragOver={(ev) => {
                    if (arrastando == null || !daColuna) return
                    ev.preventDefault()
                    ev.dataTransfer.dropEffect = "move"
                    if (sobre !== l.id) setSobre(l.id)
                  }}
                  onDrop={(ev) => { ev.preventDefault(); soltarEm(l.id) }}
                  style={{
                  borderTop: "1px solid var(--line2)",
                  background: l.mexido ? "var(--surface2)" : undefined,
                  opacity: arrastando === l.id ? 0.45 : 1,
                  boxShadow: marca,
                }}>
                  <td style={celula}>
                    <select
                      value={l.naColunaLigacao ? "ligacao" : "oposto"}
                      onChange={(e) => trocarColuna(l.id, e.target.value === "ligacao")}
                      style={{ fontSize: 11 }}
                    >
                      <option value="ligacao">ligação</option>
                      <option value="oposto">oposto</option>
                    </select>
                  </td>

                  <td style={{ ...celula, fontWeight: 600, cursor: "grab", userSelect: "none" }} {...alca}>
                    <span aria-hidden="true" style={{ color: "var(--txt3)", fontWeight: 400, marginRight: 4 }}>⠿</span>
                    {l.pino.trim() || "—"}
                  </td>

                  <td style={{ ...celula, cursor: "grab", userSelect: "none" }} {...alca}
                      title={`${l.descricaoDoOutro} — segure e arraste para mudar a ordem`}>
                    {l.codigoDoOutro || "—"}
                    {l.pinoDaLigacao ? ` · ${l.pinoDaLigacao}` : ""}
                    {l.juncao && <span style={{ color: "var(--txt3)" }}> (junção)</span>}
                    {l.ocultar && <span style={{ color: "var(--txt3)" }}> (oculto)</span>}
                  </td>

                  <td style={celula}>
                    <input
                      value={l.texto}
                      onChange={(e) => mudar(l.id, { texto: e.target.value })}
                      style={{ width: 90, fontSize: 11 }}
                    />
                  </td>

                  <td style={celula}>
                    <input
                      type="number" value={l.altura}
                      onChange={(e) => mudar(l.id, { altura: Number(e.target.value) })}
                      style={{ width: 52, fontSize: 11 }}
                    />
                  </td>

                  <td style={celula}>
                    <input
                      type="checkbox" checked={l.deslocar}
                      onChange={(e) => mudar(l.id, { deslocar: e.target.checked })}
                    />
                  </td>

                  <td style={celula}>
                    <input
                      type="number" value={l.ramificacaoPosicao} min={0}
                      onChange={(e) => mudar(l.id, { ramificacaoPosicao: Number(e.target.value) })}
                      style={{ width: 46, fontSize: 11 }}
                    />
                  </td>

                  <td style={celula}>
                    <select
                      value={l.tipoCircuito}
                      onChange={(e) => mudar(l.id, { tipoCircuito: Number(e.target.value) })}
                      disabled={l.circuitoId === 0}
                      title={l.circuitoId === 0 ? "este terminal não tem circuito" : undefined}
                      style={{ fontSize: 11, maxWidth: 130 }}
                    >
                      {TIPOS_DE_CIRCUITO.map(([v, r]) => (
                        <option key={v} value={v}>{r}</option>
                      ))}
                    </select>
                  </td>

                  <td style={celula}>
                    <input
                      value={l.apelidoCircuito}
                      onChange={(e) => mudar(l.id, { apelidoCircuito: e.target.value })}
                      disabled={l.circuitoId === 0}
                      style={{ width: 70, fontSize: 11 }}
                    />
                  </td>

                  <td style={celula}>
                    <button onClick={() => mover(l.id, -1)} style={{ fontSize: 10 }} title="subir">↑</button>
                    <button onClick={() => mover(l.id, 1)} style={{ fontSize: 10 }} title="descer">↓</button>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer style={{
        display: "flex", alignItems: "center", gap: 10, marginTop: 9, flexWrap: "wrap",
        // Com alteração pendente, o rodapé chama a atenção: sem gravar, nada fica.
        ...(mexidos > 0 ? {
          padding: "8px 10px", borderRadius: 7,
          background: "var(--acao-soft)", color: "var(--acao-on-soft)", border: "1px solid var(--acao)",
        } : {}),
      }}>
        <button onClick={salvar} disabled={gravar.isPending || linhas.length === 0}
                style={mexidos > 0
                  ? { height: 28, padding: "0 13px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                      background: "var(--brand)", color: "#FFFFFF", cursor: "pointer" }
                  : { fontSize: 11 }}>
          {gravar.isPending ? "Gravando…" : mexidos > 0 ? `Gravar ${mexidos} alteraç${mexidos === 1 ? "ão" : "ões"}` : "Gravar a geometria"}
        </button>
        <span style={{ fontSize: 10.5, color: mexidos > 0 ? undefined : "var(--txt3)" }}>
          {mexidos > 0
            ? <b>Há alteração não gravada — sem gravar, ela se perde ao fechar.</b>
            : <>grava {linhas.length === 1 ? "o terminal" : `os ${linhas.length} terminais`} e a ordem das duas colunas</>}
        </span>
        {recado && (
          <span style={{
            fontSize: 11,
            color: gravar.isError ? "var(--erro)" : "var(--txt2)",
          }}>{recado}</span>
        )}
      </footer>
    </section>
  )
}
